import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { Toaster } from 'sonner';
import { LogOut, Image, MessageSquareQuote, FileText, ExternalLink, HelpCircle, Sliders, Layers, Settings, Building2, Archive, Type, FolderOpen, Scale, Clapperboard } from 'lucide-react';
import GalleryManager from './GalleryManager';
import TestimonialsManager from './TestimonialsManager';
import QuotesManager from './QuotesManager';
import FAQManager from './FAQManager';
import SitePhotosManager from './SitePhotosManager';
import FormSettingsManager from './FormSettingsManager';
import SiteSettingsManager from './SiteSettingsManager';
import ArchivesManager from './ArchivesManager';
import ContentManager from './ContentManager';
import PartnersManager from './PartnersManager';
import ProjectsManager from './ProjectsManager';
import LegalManager from './LegalManager';
import OpeningManager from './OpeningManager';

const TABS = [
  { id: 'quotes', label: 'Demandes', icon: FileText },
  { id: 'opening', label: 'Ouverture', icon: Clapperboard },
  { id: 'sitephotos', label: 'Photos du site', icon: Layers },
  { id: 'gallery', label: 'Galerie', icon: Image },
  { id: 'projects', label: 'Réalisations', icon: FolderOpen },
  { id: 'testimonials', label: 'Témoignages', icon: MessageSquareQuote },
  { id: 'partners', label: 'Partenaires', icon: Building2 },
  { id: 'faq', label: 'FAQ', icon: HelpCircle },
  { id: 'formsettings', label: 'Formulaires', icon: Sliders },
  { id: 'sitesettings', label: 'Site', icon: Settings },
  { id: 'content', label: 'Textes', icon: Type },
  { id: 'legal', label: 'Pages légales', icon: Scale },
  { id: 'archives', label: 'Archives', icon: Archive },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('quotes');

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="admin-dashboard">
      <Toaster />
      <header className="bg-[#FFFFFF] text-[#111111] border-b border-[#111111]/10">
        <div className="container-custom flex justify-between items-center py-6">
          <div className="flex items-center gap-12">
            <Link to="/" className="font-serif text-xl tracking-widest uppercase">JBMH Events</Link>
            <span className="hidden md:inline text-[10px] uppercase tracking-[0.4em] text-[#111111] border-l border-[#111111]/20 pl-12">Administration</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" target="_blank" className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#111111]/70 hover:text-[#6B6B6B] transition-colors">
              Voir le site <ExternalLink className="w-3 h-3" />
            </Link>
            <span className="hidden md:inline text-xs text-[#111111]/60">{user?.email}</span>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#111111]/70 hover:text-[#6B6B6B] transition-colors"
            >
              <LogOut className="w-4 h-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b border-[#111111]/10 sticky top-0 z-30">
        <div className="container-custom flex gap-2 overflow-x-auto lg:flex-wrap lg:overflow-visible">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-5 py-5 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors whitespace-nowrap ${
                  active ? 'border-[#111111] text-[#111111]' : 'border-transparent text-[#6B6B6B] hover:text-[#111111]'
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <main className="container-custom py-12">
        {tab === 'quotes' && <QuotesManager />}
        {tab === 'opening' && <OpeningManager />}
        {tab === 'sitephotos' && <SitePhotosManager />}
        {tab === 'gallery' && <GalleryManager />}
        {tab === 'projects' && <ProjectsManager />}
        {tab === 'testimonials' && <TestimonialsManager />}
        {tab === 'partners' && <PartnersManager />}
        {tab === 'faq' && <FAQManager />}
        {tab === 'formsettings' && <FormSettingsManager />}
        {tab === 'sitesettings' && <SiteSettingsManager />}
        {tab === 'content' && <ContentManager />}
        {tab === 'legal' && <LegalManager />}
        {tab === 'archives' && <ArchivesManager />}
      </main>
    </div>
  );
};

export default AdminDashboard;
